import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ClassicPreset } from 'rete';
import { TooltipModule } from 'primeng/tooltip';
import { parseApiError } from '../../quotes-api-error';
import { MouldingGraphService } from '../services/moulding-graph.service';
import { MouldingProcessingService } from '../services/moulding-processing.service';
import {
  AsyncNodeKind,
  isAsyncNodeKind,
  NodeKind,
} from '../nodes/moulding-node-registry';
import { MouldingNodeDialogService } from '../services/moulding-node-dialog.service';

type NodeData = ClassicPreset.Node & { width?: number; height?: number };

@Component({
  selector: 'app-moulding-node-content',
  standalone: true,
  imports: [TooltipModule],
  templateUrl: './moulding-node-content.component.html',
  styleUrl: './moulding-node-content.component.scss',
})
export class MouldingNodeContentComponent {
  @Input() data!: NodeData;
  @Output() readonly layoutChanged = new EventEmitter<void>();

  private readonly graph = inject(MouldingGraphService);
  readonly processing = inject(MouldingProcessingService);
  private readonly dialogs = inject(MouldingNodeDialogService);
  private readonly cdr = inject(ChangeDetectorRef);

  startingProcessingNode = false;
  processingNodeStartError: string | null = null;
  editingName = false;
  nodeNameDraft = '';

  get selected(): boolean {
    return this.data.selected ?? false;
  }

  get nodeKind(): NodeKind | null {
    return this.graph.getNodeKind(String(this.data.id));
  }

  get nodeSummary(): string {
    return this.graph.getNodeSummary(String(this.data.id));
  }

  get isOutdated(): boolean {
    return this.graph.isNodeOutdated(this.nodeId);
  }

  get nodeId(): string {
    return String(this.data.id);
  }

  get processingNodeKind(): AsyncNodeKind | null {
    const kind = this.nodeKind;
    return isAsyncNodeKind(kind) ? kind : null;
  }

  get canStartProcessingNode(): boolean {
    return (
      !this.startingProcessingNode &&
      this.processingNodeKind !== null &&
      this.processing.canStartProcessingNode(
        this.nodeId,
        this.processingNodeKind,
      )
    );
  }

  get processingNodeActionLabel(): string {
    const kind = this.processingNodeKind;
    if (!kind) {
      return 'Start step';
    }
    const status = this.graph.getNodeData(this.nodeId, kind)?.status;
    return status === 'complete' || status === 'error'
      ? 'Restart step'
      : 'Start step';
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    void this.graph.deleteNode(this.nodeId);
  }

  beginNameEdit(event: Event): void {
    event.stopPropagation();
    if (this.editingName) {
      return;
    }
    this.nodeNameDraft = this.data.label;
    this.editingName = true;
    this.detectChangesAndUpdateLayout();
  }

  cancelNameEdit(event: Event): void {
    event.stopPropagation();
    this.editingName = false;
    this.detectChangesAndUpdateLayout();
  }

  async saveNodeName(event: Event): Promise<void> {
    event.stopPropagation();
    await this.graph.updateNodeName(this.nodeId, this.nodeNameDraft);
    this.editingName = false;
    this.detectChangesAndUpdateLayout();
  }

  updateNodeNameDraft(event: Event): void {
    this.nodeNameDraft = (event.target as HTMLInputElement).value;
  }

  openNodeDialog(event: Event): void {
    event.stopPropagation();
    const nodeKind = this.nodeKind;
    if (nodeKind) {
      this.dialogs.open(this.nodeId, nodeKind);
    }
  }

  onNodeBodyClick(event: MouseEvent): void {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('.input-socket, .output-socket, .input-control')
    ) {
      return;
    }

    this.openNodeDialog(event);
  }

  onNodeBodyKeydown(event: KeyboardEvent): void {
    if (
      event.target !== event.currentTarget ||
      (event.key !== 'Enter' && event.key !== ' ')
    ) {
      return;
    }

    event.preventDefault();
    this.openNodeDialog(event);
  }

  async onStartProcessingNode(event: Event): Promise<void> {
    const kind = this.processingNodeKind;
    if (!kind || !this.canStartProcessingNode) {
      return;
    }

    event.stopPropagation();
    this.startingProcessingNode = true;
    this.processingNodeStartError = null;
    this.cdr.detectChanges();
    try {
      await this.processing.startProcessingNode(this.nodeId, kind);
    } catch (error) {
      this.processingNodeStartError = parseApiError(
        error,
        'Failed to start the step.',
      );
    } finally {
      this.startingProcessingNode = false;
      this.cdr.detectChanges();
    }
  }

  private detectChangesAndUpdateLayout(): void {
    this.cdr.detectChanges();
    this.layoutChanged.emit();
  }
}
